import { useDisplayedCarouselItem } from '@/hooks/use-displayed-carousel-item';
import { AnnounceLiveRegion } from '@rhg/hooks/use-announce';
import commonMessages from '@rhg/lang/messages/common';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

/**
 * WCAG 4.1.3 Status Messages: the Back/Forward buttons — and the `autoScroll`
 * timer on carousels that use it — only change scroll position (no content is
 * added/removed/hidden), so there's nothing for a screen reader to pick up on
 * its own when either fires. This always-mounted, visually-hidden live region
 * announces the resulting position so assistive tech gets *some* confirmation
 * something happened. It stays empty until `pendingAnnouncementRef` is set by
 * a Back/Forward click or an auto-scroll tick, so there's no announcement on
 * initial mount.
 *
 * Holds its own state rather than using `useAnnounce`: this effect is keyed on
 * `displayedItemIndex`, so the same position can't be announced twice in a row
 * and there's nothing for `announce()`'s clear-then-set to fix.
 */
export function CarouselStatusAnnouncer({
    total,
    pendingAnnouncementRef,
}: {
    total: number;
    pendingAnnouncementRef: { current: boolean };
}) {
    const { formatMessage } = useIntl();
    const [announcement, setAnnouncement] = useState('');
    const { displayedItemIndex } = useDisplayedCarouselItem();

    useEffect(() => {
        if (
            !pendingAnnouncementRef.current ||
            displayedItemIndex === undefined
        ) {
            return;
        }
        pendingAnnouncementRef.current = false;
        setAnnouncement(
            formatMessage(commonMessages['common.carousel_position_status'], {
                current: displayedItemIndex + 1,
                total,
            })
        );
    }, [displayedItemIndex, total, formatMessage, pendingAnnouncementRef]);

    return <AnnounceLiveRegion message={announcement} />;
}
